import { useState, useCallback } from 'react'
import { CheckCircle2, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/i18n'

interface BoardTourV2Props {
  isOpen: boolean
  onClose: () => void
}

export default function BoardTourV2({ isOpen, onClose }: BoardTourV2Props) {
  const { t } = useTranslation()
  const [stepIndex, setStepIndex] = useState(0)

  const TOUR_STEPS = [
    {
      id: 'welcome',
      title: t('board:tour.welcome_title', { defaultValue: "You're all set" }),
      description: t('board:tour.welcome_description', {
        defaultValue:
          "Chat with your AI, feed it your knowledge, and let it run the workflows that used to slow you down. Everything's connected just start a conversation.",
      }),
      position: 'center' as const,
    },
    {
      id: 'upload',
      title: t('board:tour.upload_title', { defaultValue: 'Feed your Intuigents to start' }),
      description: t('board:tour.upload_description', {
        defaultValue:
          'Add your data to generate a knowledge graph of your operation, so each response is grounded in verifiable sources from your own data.',
      }),
      position: 'left' as const,
    },
    {
      id: 'start',
      title: t('board:tour.start_title', {
        defaultValue: 'Build your board, add your files, and start the conversation',
      }),
      description: t('board:tour.start_description', {
        defaultValue:
          'Create a board, add files to define what your Intuigents draw on, then ask questions by chat or video calls.',
      }),
      position: 'center-bottom' as const,
    },
  ]

  const handleNext = useCallback(() => {
    if (stepIndex < TOUR_STEPS.length - 1) {
      setStepIndex(stepIndex + 1)
    } else {
      onClose()
    }
  }, [stepIndex, onClose])

  const handleSkip = useCallback(() => {
    onClose()
  }, [onClose])

  if (!isOpen) return null

  const step = TOUR_STEPS[stepIndex]
  const isLast = stepIndex === TOUR_STEPS.length - 1
  const isFirst = stepIndex === 0

  // Position classes based on step
  const positionClass = step.position === 'left'
    ? 'top-1/2 left-4 -translate-y-1/2 max-w-[320px]'
    : step.position === 'center-bottom'
      ? 'bottom-24 left-1/2 -translate-x-1/2 max-w-[420px]'
      : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[460px]'

  return (
    <div className="fixed inset-0 z-[9998]">
      {/* Semi-transparent backdrop */}
      <div className="absolute inset-0 bg-black/20" />

      {/* Tour card */}
      <div className={`absolute ${positionClass} z-10`}>
        <div className="bg-white dark:bg-dark-card rounded-2xl shadow-2xl p-6 flex flex-col items-center text-center">
          {/* Icon for welcome step */}
          {isFirst && (
            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-dark-muted flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6 text-gray-500" />
            </div>
          )}

          <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{step.description}</p>

          {/* Progress dots */}
          <div className="flex items-center gap-1.5 mb-4">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={`rounded-full transition-all ${
                  i === stepIndex ? 'w-5 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3">
            {!isLast && (
              <Button variant="ghost" size="sm" onClick={handleSkip} className="text-muted-foreground">
                {t('board:tour.skip', { defaultValue: 'Skip' })}
              </Button>
            )}
            <Button size="sm" onClick={handleNext}>
              {isFirst ? t('board:tour.start_tour', { defaultValue: 'Start Tour' }) : isLast ? (
                <>{t('board:tour.start_exploring', { defaultValue: 'Start Exploring' })} <ArrowRight className="w-4 h-4 ml-1" /></>
              ) : t('board:tour.continue', { defaultValue: 'Continue' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
